import styled from '@emotion/styled';
import { Button } from '@node-real/uikit';
import { useState } from 'react';
import { TokenObjectType } from '@op-bridge/bridge-core';

import { reportEvent, GA_MAP } from '../../utils/ga';
import { useApproveAutoWithdraw, useGetAutoWithdrawAllowance, usePending } from '../../hooks';
import { ButtonWarning } from '../ButtonWarning';
import { WaitingForConfirmation } from '../modal/WaitingForConfirmation';
import { batchUpdate } from '../../utils';
import { TxError } from '../modal/TxError';
export const ApproveL2TokenButton = ({
  amount,
  asset,
  allowance,
  tokenType,
  handleAllowanceUpdate,
  disabled,
}: {
  amount: string;
  asset: TokenObjectType[];
  allowance: number;
  tokenType: string;
  handleAllowanceUpdate: (amount: number) => void;
  disabled: boolean;
}) => {
  const { getL2Allowance } = useGetAutoWithdrawAllowance();
  const { showPending, handlePendingShow } = usePending();
  const {
    approveL2Token,
    approveError,
    showError,
    handleErrorShow,
    waitingConfirmation,
    setWaitingConfirmation,
  } = useApproveAutoWithdraw();

  const [txFailedMsg, setTxFailedMsg] = useState('');

  return (
    <>
      <StyledButton
        disabled={disabled}
        color={'bg.card'}
        onClick={() => {
          reportEvent({
            name: GA_MAP.autoWithdrawApprove,
            data: { name: 'Click ERC20 Approve' },
          });
          try {
            setWaitingConfirmation(true);
            handlePendingShow(true);
            approveL2Token(amount, tokenType)
              .then(() => {
                getL2Allowance(tokenType).then((res: string) => {
                  if (res) {
                    // console.log('Approve ERC20 update allowance amount to', Number(res));
                    handleAllowanceUpdate(Number(res) * Math.pow(10, -18));
                  }
                  if (waitingConfirmation === true) {
                    setWaitingConfirmation(false);
                  }
                  handlePendingShow(false);
                });
              })
              .catch((e: any) => {
                // eslint-disable-next-line no-console
                console.log(e?.code);
                if (e && e?.code) {
                  // do not show error if user accelerate tx (tx hash replaced)
                  if (e?.code === 'TRANSACTION_REPLACED' && e?.replacement?.hash) {
                    // eslint-disable-next-line no-console
                    console.log(e.replacement);
                  } else {
                    setTxFailedMsg(e?.code);
                  }
                } else {
                  setTxFailedMsg(e);
                }
                handleErrorShow(true);
                batchUpdate(() => {
                  setWaitingConfirmation(false);
                  handlePendingShow(false);
                });
              });
          } catch (e: any) {
            if (e && e?.code) {
              // do not show error if user accelerate tx (tx hash replaced)
              if (e?.code === 'TRANSACTION_REPLACED' && e?.replacement?.hash) {
                // eslint-disable-next-line no-console
                console.log(e.replacement);
              } else {
                setTxFailedMsg(e?.code);
              }
            } else {
              setTxFailedMsg(e);
            }
            handleErrorShow(true);
            batchUpdate(() => {
              setWaitingConfirmation(false);
              handlePendingShow(false);
            });
            // eslint-disable-next-line no-console
            console.log(e);
          }
        }}
      >
        Approve
      </StyledButton>
      <ButtonWarning
        text={
          allowance > 0
            ? `Exceeds the spending cap set for ${tokenType}. Approve resetting it to proceed.`
            : `Please approve setting a spending cap for ${asset[0].name} first.
            `
        }
      ></ButtonWarning>
      {waitingConfirmation ? (
        <WaitingForConfirmation
          isOpen={showPending}
          handleOpen={handlePendingShow}
        ></WaitingForConfirmation>
      ) : null}

      {(txFailedMsg || approveError) && (
        <TxError
          isOpen={showError}
          title="Transaction Failed"
          handleOpen={() => {
            handleErrorShow(false);
          }}
          errorCode={txFailedMsg || approveError}
        />
      )}
    </>
  );
};

const StyledButton = styled(Button)`
  line-height: 20px;
  height: 48px;
  width: 100%;
  margin-top: -12px;
  margin-bottom: 8px;
  border-radius: 8px;
  background: ${(props: any) => props.theme.colors.scene.orange.normal};
  color: white;
  &:hover {
    background: ${(props: any) => props.theme.colors.scene.orange.active};
  }
  &[disabled] {
    &,
    &:hover {
      color: ${(props: any) => props.theme.colors.readable?.disabled};
      background: ${(props: any) => props.theme.colors.bg?.bottom};
      opacity: 1;
    }
  }
`;
